import { useCallback, useEffect, useRef, useState } from 'react';
import type { DiscordGuildChannel } from '@playerbase/types';

import { fetchDiscordGuildChannels } from './discord-channels-data.js';
import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Reads discord_guild_channels for one connection and keeps the local copy
// in sync via Realtime so the picker reflects renames / additions / deletes
// the moment the bot mirrors them, without the user having to refresh.
//
// Two states the caller distinguishes:
//   - loading=true on the very first load (before Realtime has fired)
//   - loading=false thereafter; refetches happen silently so the list
//     doesn't flash a placeholder on every keystroke-style change
// ---------------------------------------------------------------------------

interface UseDiscordGuildChannels {
  channels: DiscordGuildChannel[];
  loading: boolean;
  reload: () => Promise<void>;
}

export function useDiscordGuildChannels(connectionId: string | null): UseDiscordGuildChannels {
  const [channels, setChannels] = useState<DiscordGuildChannel[]>([]);
  const [loading, setLoading] = useState(true);

  // Track the latest connection id in a ref so the Realtime callback always
  // refetches against the connection it was opened for, not a stale closure.
  const connectionIdRef = useRef(connectionId);
  useEffect(() => {
    connectionIdRef.current = connectionId;
  }, [connectionId]);

  const reload = useCallback(async (): Promise<void> => {
    const id = connectionIdRef.current;
    if (id === null) {
      setChannels([]);
      setLoading(false);
      return;
    }
    const next = await fetchDiscordGuildChannels(id);
    setChannels(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (connectionId === null) {
      setChannels([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void reload();
  }, [connectionId, reload]);

  useEffect(() => {
    if (connectionId === null) return undefined;
    const channel = supabase.channel(`discord-guild-channels:${connectionId}`);
    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discord_guild_channels',
          filter: `connection_id=eq.${connectionId}`,
        },
        () => {
          void reload();
        },
      )
      .subscribe();
    return () => {
      void channel.unsubscribe();
    };
  }, [connectionId, reload]);

  return { channels, loading, reload };
}
